import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.4)"
      : "rgba(0, 0, 0, 0.7)"};
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 20px;
`;

export const ModalContainer = styled.div`
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(255, 255, 255, 0.98)"
      : "rgba(20, 23, 29, 0.98)"};
  border: 1px solid ${({ theme }) => theme.border2};
  border-radius: 20px;
  box-shadow: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "0 20px 60px rgba(0, 0, 0, 0.15), 0 0 100px rgba(16, 185, 129, 0.05)"
      : "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(16, 185, 129, 0.1)"};
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.3s ease-out;
  overflow: hidden;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid ${({ theme }) => theme.border2};
  background: ${({ theme }) => theme.contrast};
`;

export const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;

  svg {
    font-size: 24px;
    color: ${({ theme }) => theme.primary};
  }
`;

export const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: ${({ theme }) => theme.buttonBg};
  border: 1px solid ${({ theme }) => theme.border2};
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.buttonHover};
    opacity: 1;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const SearchBox = styled.div`
  padding: 20px 28px;
  border-bottom: 1px solid ${({ theme }) => theme.border2};
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${({ theme }) => theme.contrast};

  .search-icon {
    font-size: 20px;
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
  }

  input {
    flex: 1;
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.text};
    font-size: 15px;
    font-family: inherit;
    outline: none;

    &::placeholder {
      color: ${({ theme }) => theme.text};
      opacity: 0.3;
    }
  }
`;

export const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.contrast};
    border-radius: 12px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border2};
    border-radius: 12px;

    &:hover {
      background: ${({ theme }) => theme.text};
      opacity: 0.2;
    }
  }
`;

export const ProjectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ProjectItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: ${props => props.isActive 
    ? 'rgba(16, 185, 129, 0.1)' 
    : props.theme.card};
  border: 1px solid ${props => props.isActive 
    ? 'rgba(16, 185, 129, 0.3)' 
    : props.theme.border2};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.isActive 
      ? 'rgba(16, 185, 129, 0.15)' 
      : props.theme.card2};
    border-color: ${props => props.isActive 
      ? 'rgba(16, 185, 129, 0.4)' 
      : props.theme.border};
    transform: translateX(4px);
  }

  &:active {
    transform: translateX(4px) scale(0.98);
  }
`;

export const ProjectIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.isActive 
    ? (props.color ? `${props.color}15` : 'rgba(16, 185, 129, 0.1)') 
    : props.theme.body === '#fafbfc' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.isActive 
    ? (props.color ? `${props.color}40` : 'rgba(16, 185, 129, 0.3)') 
    : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: ${props => props.isActive 
    ? `0 4px 12px ${props.color ? `${props.color}30` : 'rgba(16, 185, 129, 0.3)'}`
    : 'none'};
  transition: all 0.2s ease;

  svg {
    font-size: 24px;
    color: ${props => props.color || (props.isActive ? '#10b981' : props.theme.text)};
    opacity: ${props => props.isActive ? '1' : '0.5'};
  }
`;

export const ProjectInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
`;

export const ProjectName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ProjectId = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Courier New', monospace;
`;

export const CheckIcon = styled.div`
  flex-shrink: 0;
  
  svg {
    font-size: 28px;
    color: ${({ theme }) => theme.primary};
    filter: drop-shadow(0 2px 4px rgba(16, 185, 129, 0.3));
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;

  svg {
    font-size: 64px;
    color: ${({ theme }) => theme.text};
    opacity: 0.2;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    margin: 0 0 8px 0;
  }

  span {
    font-size: 14px;
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
  }
`;
